import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from "../../contexts/AuthContext.jsx";
import { useEnvironment } from '../../contexts/EnvironmentContext';
import HomeNavMenu from './HomeNavMenu';
import Breadcrumbs from './Breadcrumbs.jsx';
import { getVersion } from '../../services/versionService.js';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout.js';
import { isParticipantDomain } from '../../utils/participantDomain.js';


const Layout = ({ children, isDev }) => {
  const { isAuthenticated, setIsAuthenticated, userType, user } = useContext(AuthContext);
  const { isDemo } = useEnvironment();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [version, setVersion] = useState(null);
  const [isScreenBuilder, setIsScreenBuilder] = useState(false);
  const [isLocalDevMode, setIsLocalDevMode] = useState(() => isLocalDevLayoutActive());
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  // Stay aligned with LocalDevBanner + screen builder (same rules as isLocalDevLayoutActive)
  useEffect(() => {
    const sync = () => setIsLocalDevMode(isLocalDevLayoutActive());

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const isParticipant = userType === 'participant';
  const isSponsorSelector = location.pathname === '/sponsor-selector';
  // Caregiver-facing routes are unauthenticated-or-caregiver-token surfaces and
  // share none of the staff chrome (no sidebar, breadcrumbs, header, footer).
  const isCaregiverRoute =
    location.pathname.startsWith('/caregiver-login') ||
    location.pathname.startsWith('/caregiver/');
  const noSidebarPaths = ['/', '/login', '/forgot-password', '/register', '/sponsor-selector'];
  const noBreadcrumbPaths = ['/reset-password', '/profile', '/development', '/caregiver-login', '/caregiver'];
  const noHeaderPaths = ['/login', '/forgot-password', '/reset-password', '/caregiver-login', '/caregiver'];
  const shouldShowSidebar = isAuthenticated && !isParticipant && !isCaregiverRoute && !noSidebarPaths.includes(location.pathname);
  const shouldRenderBreadcrumbs = !pathnames.includes('auth') && !isCaregiverRoute;
  const isStudiesRootPath = location.pathname === '/studies';
  const isCompletedSurveysPath = location.pathname.startsWith('/completed-surveys');
  const isShallowPath = pathnames.length <= 1;
  // Assessment builder detail route (/assessment-builder/:assessmentId) only shows
  // a raw UUID in the breadcrumb and flashes during the builder's load spinner, so
  // suppress breadcrumbs there entirely.
  const isAssessmentBuilderDetailPath = pathnames[0] === 'assessment-builder' && pathnames.length > 1;
  // Review routes (read-only approval surface). The naive path-segment breadcrumb
  // turns 'review' into a bogus study-name link that loads a "review" draft and
  // errors, so suppress breadcrumbs on the whole review flow.
  const isReviewPath =
    (pathnames[0] === 'studybuilder' || pathnames[0] === 'assessment-builder') &&
    (pathnames[1] === 'review' || pathnames[1] === 'review-queue');

  // Check for screenbuilder-active class on body
  useEffect(() => {
    const checkScreenBuilderClass = () => {
      setIsScreenBuilder(document.body.classList.contains('screenbuilder-active'));
    };
    
    // Check immediately
    checkScreenBuilderClass();
    
    // Set up a MutationObserver to watch for class changes
    const observer = new MutationObserver(checkScreenBuilderClass);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Using async function to fetch API version
      const fetchVersion = async () => {
        try {
          const apiVersion = await getVersion();
          setVersion(apiVersion);
        } catch (error) {
          console.error('Error fetching version:', error);
          setVersion(null);
        }
      };
      
      fetchVersion();
    } else {
      setVersion(null);
    }
  }, [isAuthenticated]);

  // When local dev banner is visible (32px), increase top padding to account for it.
  // Caregiver routes own their own layout end-to-end (including absorbing the
  // local-dev banner via their own pt-8 styling), so Layout adds no padding.
  const topPadding = isCaregiverRoute
    ? ''
    : isScreenBuilder
    ? (isLocalDevMode ? 'pt-8' : '')
    : (isLocalDevMode ? 'pt-24 md:pt-28' : 'pt-16 md:pt-20');

  const participantPortalForceLight =
    location.pathname === '/login/participant' ||
    location.pathname.startsWith('/home/') ||
    (location.pathname === '/' && isParticipantDomain());

  return (
    <div
      className={`min-h-screen ${isParticipant ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-900'} relative ${topPadding} theme-transition ${participantPortalForceLight ? 'force-light-mode' : ''}`}
    >
      {/* Accessibility: skip-to-content link */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <div>
        {!isScreenBuilder && !noHeaderPaths.some(path => location.pathname.startsWith(path)) && (
          <HomeNavMenu 
            isAuthenticated={isAuthenticated} 
            setIsAuthenticated={setIsAuthenticated}
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
            isDev={isDev}
          />
        )}

        {!isScreenBuilder && shouldShowSidebar && !isSponsorSelector && shouldRenderBreadcrumbs && !isStudiesRootPath && !isCompletedSurveysPath && !isShallowPath && !isAssessmentBuilderDetailPath &&
         !isReviewPath &&
         !noBreadcrumbPaths.some(path => location.pathname.startsWith(path)) && (
          <nav className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/30" aria-label="Breadcrumb">
            <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
              <Breadcrumbs />
            </div>
          </nav>
        )}
        <main
          id="main-content"
          role="main"
          className={`${isScreenBuilder ? '' : 'max-w-7xl mx-auto py-6 sm:px-6 lg:px-8'} ${isParticipant ? 'px-4' : ''}`}
        >
          <div className="flex-grow">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
